import type { AcraEntityRecord, AcraEntitySearchResult, AcraSsicCatalog } from './dataGovSg';
import { formatAcraAddress, parseAcraEntityRecord } from './dataGovSg';
import { getDbSupabase, isSupabaseConfigured } from './supabase';

export type AcraEntityHint = {
  status: string;
  registeredAddress: string;
};

export type AcraEntitiesSyncState = {
  status: string;
  syncStartedAt: string | null;
  syncCompletedAt: string | null;
  lastError: string | null;
  rowsSynced: number;
  currentBucket: string | null;
  currentOffset: number;
  entityCount: number;
};

function asRecord(value: unknown, fallback?: { entity_name?: string | null; entity_status?: string | null }): AcraEntityRecord | null {
  if (!value || typeof value !== 'object') return null;
  const parsed = parseAcraEntityRecord(value as Record<string, unknown>);
  if (!parsed.uen) return null;
  if (!parsed.entityName && fallback?.entity_name?.trim()) {
    parsed.entityName = fallback.entity_name.trim();
  }
  // Indexed column is updated on every sync; payload.status can lag behind.
  if (fallback?.entity_status?.trim()) {
    parsed.status = fallback.entity_status.trim();
  }
  return parsed;
}

let syncStateCache: { at: number; value: AcraEntitiesSyncState | null } | null = null;
const SYNC_STATE_TTL_MS = 60_000;

export async function getAcraEntitiesSyncState(): Promise<AcraEntitiesSyncState | null> {
  if (!isSupabaseConfigured) return null;
  if (syncStateCache && Date.now() - syncStateCache.at < SYNC_STATE_TTL_MS) {
    return syncStateCache.value;
  }
  const crm = getDbSupabase('crm');
  const { data, error } = await crm.rpc('get_acra_entities_sync_state');
  if (error) {
    syncStateCache = { at: Date.now(), value: null };
    return null;
  }
  const row = (data ?? {}) as Record<string, unknown>;
  const value: AcraEntitiesSyncState = {
    status: String(row.status ?? 'idle'),
    syncStartedAt: typeof row.sync_started_at === 'string' ? row.sync_started_at : null,
    syncCompletedAt: typeof row.sync_completed_at === 'string' ? row.sync_completed_at : null,
    lastError: typeof row.last_error === 'string' ? row.last_error : null,
    rowsSynced: Number(row.rows_synced) || 0,
    currentBucket: typeof row.current_bucket === 'string' ? row.current_bucket : null,
    currentOffset: Number(row.current_offset) || 0,
    entityCount: Number(row.entity_count) || 0,
  };
  syncStateCache = { at: Date.now(), value };
  return value;
}

export async function searchAcraEntitiesFromDb(opts: {
  query: string;
  offset?: number;
  limit?: number;
  primarySsicCode?: string;
  secondarySsicCode?: string;
}): Promise<AcraEntitySearchResult | null> {
  if (!isSupabaseConfigured) return null;

  // Skip the sync-state round-trip for normal typeahead; only gate SSIC catalog paths.
  const crm = getDbSupabase('crm');
  const { data, error } = await crm.rpc('search_acra_entities', {
    p_query: opts.query.trim() || null,
    p_primary_ssic: opts.primarySsicCode?.trim() || null,
    p_secondary_ssic: opts.secondarySsicCode?.trim() || null,
    p_offset: Math.max(0, opts.offset ?? 0),
    p_limit: Math.min(Math.max(opts.limit ?? 25, 1), 100),
  });
  if (error) return null;

  const payload = (data ?? {}) as Record<string, unknown>;
  const records = Array.isArray(payload.records)
    ? payload.records
        .map((row) => asRecord(row))
        .filter((row): row is AcraEntityRecord => Boolean(row))
    : [];

  return {
    records,
    total: Number(payload.total) || 0,
    offset: Number(payload.offset) || 0,
    limit: Number(payload.limit) || (opts.limit ?? 25),
  };
}

export async function loadAcraSsicCatalogFromDb(): Promise<AcraSsicCatalog | null> {
  if (!isSupabaseConfigured) return null;

  const state = await getAcraEntitiesSyncState();
  if (!state || state.entityCount <= 0) return null;

  const crm = getDbSupabase('crm');
  const { data, error } = await crm.rpc('get_acra_ssic_catalog');
  if (error || !data) return null;

  const row = data as Record<string, unknown>;
  const primary = Array.isArray(row.primary) ? row.primary : [];
  const secondary = Array.isArray(row.secondary) ? row.secondary : [];

  return {
    fetchedAt: typeof row.fetched_at === 'string' ? row.fetched_at : new Date().toISOString(),
    primary: primary
      .map((item) => {
        const code = String((item as Record<string, unknown>).code ?? '').trim();
        const description = String((item as Record<string, unknown>).description ?? '').trim();
        return code ? { code, description } : null;
      })
      .filter((item): item is { code: string; description: string } => Boolean(item)),
    secondary: secondary
      .map((item) => {
        const code = String((item as Record<string, unknown>).code ?? '').trim();
        const description = String((item as Record<string, unknown>).description ?? '').trim();
        return code ? { code, description } : null;
      })
      .filter((item): item is { code: string; description: string } => Boolean(item)),
  };
}

export async function getAcraEntityCountFromDb(): Promise<number | null> {
  const state = await getAcraEntitiesSyncState();
  if (!state || state.entityCount <= 0) return null;
  return state.entityCount;
}

export async function getAcraEntityByUen(uen: string): Promise<AcraEntityRecord | null> {
  if (!isSupabaseConfigured) return null;
  const normalized = uen.trim().toUpperCase();
  if (!normalized) return null;

  const crm = getDbSupabase('crm');
  const { data, error } = await crm
    .from('acra_entities')
    .select('payload, entity_name, entity_status')
    .eq('uen', normalized)
    .maybeSingle();
  if (error || !data?.payload) return null;
  return asRecord(data.payload, data);
}

const ACRA_NAME_STOP = new Set([
  'PTE',
  'LTD',
  'LIMITED',
  'COMPANY',
  'CO',
  'ENTERPRISE',
  'ENTERPRISES',
  'THE',
  'AND',
  'GROUP',
  'HOLDING',
  'PRIVATE',
  'INC',
  'LLC',
  'PL',
  'SDN',
  'BHD',
]);

function significantNameTokens(query: string): string[] {
  return query
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((token) => token.length >= 2 && !ACRA_NAME_STOP.has(token));
}

/**
 * Fast local name search against crm.acra_entities (daily sync).
 * ANDs significant tokens in PostgREST so Postgres can narrow early —
 * avoids fetching 100 jsonb payloads then filtering in the browser.
 */
export async function searchAcraEntitiesByNameTokens(
  query: string,
  limit = 15,
): Promise<AcraEntityRecord[]> {
  if (!isSupabaseConfigured) return [];
  const tokens = significantNameTokens(query);
  if (!tokens.length) return [];

  // Prefer longer / more distinctive tokens first (better selectivity).
  const ordered = [...tokens].sort((a, b) => b.length - a.length).slice(0, 4);
  const crm = getDbSupabase('crm');
  let req = crm
    .from('acra_entities')
    .select('payload, entity_name, entity_status')
    .limit(Math.min(Math.max(limit, 12), 25));

  for (const token of ordered) {
    req = req.ilike('entity_name', `%${token}%`);
  }

  const { data, error } = await req;
  if (error || !data?.length) return [];

  const normQuery = tokens.join(' ');
  return data
    .map((row) => {
      const record = asRecord(row.payload, row);
      if (!record) return null;
      const name = record.entityName.toUpperCase().replace(/[^A-Z0-9]+/g, ' ');
      let score = ordered.filter((token) => name.includes(token)).length * 40;
      if (name.includes(normQuery)) score += 200;
      if (/live|register/i.test(record.status)) score += 8;
      return { record, score };
    })
    .filter((row): row is { record: AcraEntityRecord; score: number } => Boolean(row))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.record.entityName.localeCompare(b.record.entityName);
    })
    .map((row) => row.record);
}

/** Batch status + registered address for account list district fallback. */
export async function getAcraEntityHintsByUens(
  uens: string[],
): Promise<Map<string, AcraEntityHint>> {
  const map = new Map<string, AcraEntityHint>();
  if (!isSupabaseConfigured) return map;

  const unique = [...new Set(uens.map((uen) => uen.trim().toUpperCase()).filter(Boolean))];
  if (!unique.length) return map;

  const crm = getDbSupabase('crm');
  const chunkSize = 200;
  for (let index = 0; index < unique.length; index += chunkSize) {
    const chunk = unique.slice(index, index + chunkSize);
    const { data, error } = await crm
      .from('acra_entities')
      .select('uen, entity_status, entity_name, payload')
      .in('uen', chunk);
    if (error) continue;
    for (const row of data ?? []) {
      const uen = String(row.uen ?? '').trim().toUpperCase();
      if (!uen) continue;
      const status = String(row.entity_status ?? '').trim();
      const record = asRecord(row.payload, row);
      const formatted = record ? formatAcraAddress(record).trim() : '';
      const registeredAddress = formatted && formatted !== '—' ? formatted : '';
      if (!status && !registeredAddress) continue;
      map.set(uen, { status, registeredAddress });
    }
  }
  return map;
}

export async function getAcraStatusesByUens(uens: string[]): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const hints = await getAcraEntityHintsByUens(uens);
  for (const [uen, hint] of hints) {
    if (hint.status) map.set(uen, hint.status);
  }
  return map;
}

export async function triggerAcraEntitiesSync(force = true): Promise<{ ok: boolean; error?: string }> {
  if (!isSupabaseConfigured) {
    return { ok: false, error: 'Supabase is not configured.' };
  }
  const crm = getDbSupabase('crm');
  const { error } = await crm.functions.invoke('acra-open-data-sync', {
    body: { force },
  });
  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}
